import { useEffect, useMemo } from 'react';
import { useRecord } from '../hooks/useRecord';

interface FieldDef {
  label: string;
  keys: string[];
  format?: (value: string) => string;
}

const PRIMARY_FIELDS: FieldDef[] = [
  { label: 'Company Name', keys: ['CompanyName', 'companyName'] },
  {
    label: 'CIN',
    keys: ['CorporateIdentificationNumber', 'cin', 'id'],
  },
  {
    label: 'Status',
    keys: ['CompanyStatus', 'companyStatus', 'status'],
  },
  {
    label: 'State',
    keys: [
      'CompanyStateCode',
      'companyStateCode',
      'RegistrationState',
      'registrationState',
      'state',
    ],
  },
  {
    label: 'NIC',
    keys: ['NicCode', 'nicCode', 'nic_code', 'ActivityCode', 'activityCode'],
  },
  {
    label: 'Activity',
    keys: ['ActivityDescription', 'activityDescription'],
  },
  { label: 'District', keys: ['District', 'district'] },
  { label: 'Address', keys: ['Address', 'address'] },
  {
    label: 'Registered Date',
    keys: [
      'DateOfIncorporation',
      'dateOfIncorporation',
      'registrationDate',
      'ApprovedYear',
    ],
    format: fmtDate,
  },
];

function fmtDate(value?: string) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

function displayValue(value: unknown, formatter?: (v: string) => string) {
  if (value === undefined || value === null || value === '') return '—';
  const str = String(value);
  return formatter ? formatter(str) : str;
}

function titleCaseLabel(key: string) {
  // Insert spaces before uppercase letters, split on underscores/spaces,
  // then capitalise each word.
  const spaced = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced
    .split(' ')
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

interface RecordDetailDrawerProps {
  recordKey: string | null;
  onClose(): void;
}

export default function RecordDetailDrawer({
  recordKey,
  onClose,
}: RecordDetailDrawerProps) {
  const isOpen = Boolean(recordKey);
  const { data, isLoading, error } = useRecord(recordKey);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const { primary, remaining } = useMemo(() => {
    if (!data) return { primary: [], remaining: [] };

    const usedKeys = new Set<string>();
    const primaryEntries = PRIMARY_FIELDS.map((field) => {
      for (const k of field.keys) {
        if (Object.prototype.hasOwnProperty.call(data, k)) usedKeys.add(k);
      }
      const key = field.keys.find((k) =>
        Object.prototype.hasOwnProperty.call(data, k)
      );
      return {
        label: field.label,
        value: displayValue(key ? data[key] : undefined, field.format),
      };
    });

    const remainingKeys = Object.keys(data)
      .filter((key) => !usedKeys.has(key))
      .sort((a, b) => a.localeCompare(b));

    const remainingEntries = remainingKeys.map((key) => ({
      label: titleCaseLabel(key),
      value: displayValue(data[key]),
    }));

    return { primary: primaryEntries, remaining: remainingEntries };
  }, [data]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-40 flex"
      role="dialog"
      aria-modal="true"
      aria-labelledby="record-detail-title"
    >
      <div
        className="fixed inset-0 bg-black/40 transition-opacity"
        onClick={onClose}
      />

      <div className="relative ml-auto flex h-full w-full max-w-2xl transform flex-col overflow-y-auto border-l border-[var(--border)] bg-[var(--card)] p-6 shadow-2xl transition-transform duration-200 ease-out">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h2
              id="record-detail-title"
              className="text-xl font-semibold text-[var(--fg)]"
            >
              Record details
            </h2>
            <p className="mt-1 text-sm text-[var(--muted)]">
              {recordKey || 'Record'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close drawer"
            className="rounded-lg p-2 text-[var(--muted)] transition hover:bg-[var(--bg)] hover:text-[var(--fg)]"
          >
            <CloseIcon />
          </button>
        </div>

        {isLoading && (
          <div className="flex flex-1 items-center justify-center text-[var(--muted)]">
            <div className="mr-3 h-5 w-5 animate-spin rounded-full border-2 border-[var(--border)] border-t-[var(--primary)]" />
            Loading record details…
          </div>
        )}

        {error && !isLoading && (
          <div className="rounded-lg border border-[var(--danger)]/30 bg-[var(--danger)]/10 px-4 py-3 text-sm text-[var(--danger)]">
            Failed to load record: {error.message}
          </div>
        )}

        {!isLoading && !error && data && (
          <dl className="divide-y divide-[var(--border)]/60">
            {primary.map(({ label, value }) => (
              <DetailItem key={label} label={label} value={value} />
            ))}
            {remaining.map(({ label, value }) => (
              <DetailItem key={label} label={label} value={value} />
            ))}
          </dl>
        )}
      </div>
    </div>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1 py-3 sm:flex-row sm:gap-4">
      <dt className="min-w-[180px] text-xs font-medium uppercase tracking-wider text-[var(--muted)]">
        {label}
      </dt>
      <dd className="flex-1 break-words text-sm text-[var(--fg)]">
        {value}
      </dd>
    </div>
  );
}

function CloseIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}
